import { Box, Divider, List, Stack, Typography, useTheme } from '@mui/material';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import React, {
  ReactNode,
  Suspense,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Await, useFetcher, useLoaderData, useLocation } from 'react-router';
import { Rule, Status, Violation } from 'types';

import ViolationListItem from 'components/lists/listItems/ViolationListItem';
import MobileViolationsSkeleton from 'components/skeletons/MobileViolations';
import ViolationListItemSkeleton from 'components/skeletons/ViolationListItem';
import useDeviceType from 'hooks/useDeviceType';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'router';
import { smallTableRowSx } from 'utils';
import AuthService from 'utils/AuthService';
import { ViolationInUseDialog } from './ViolationInUseDialog';
import CreateViolation from './CreateViolationDialog';
import MobileViolations from './MobileViolations';
import { ViolationsActionResponse } from './violationsAction';
import { ViolationsLoaderData } from './violationsLoader';

import { Add, Lock } from '@mui/icons-material';
import DetailsHeader from 'components/layout/DetailsHeader';
import FormFrame from 'components/layout/FormFrame';

export enum ViolationsActionHandlers {
  CREATE = 'handleCreateViolation',
  EXCLUDE = 'handleExcludeViolation',
  INCLUDE = 'handleIncludeViolation',
  DELETE = 'handleDeleteViolation',
}

const Violations = (): ReactNode => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const displayToast = useContext(ToastContext);
  const { type } = useDeviceType();
  const theme = useTheme();
  const isMobile = ['phone', 'tablet'].includes(type);
  const fetcher = useFetcher({ key: 'violations' });
  const { state, dispatch } = useContext(AppContext);
  const { _excludedViolations, _tenantViolations, _tenantRules } =
    useLoaderData() as ViolationsLoaderData;
  const allowCreate =
    AuthService.IsSuperAdmin() || state.entityPermissions.rule.create;
  const allowUpdate =
    AuthService.IsSuperAdmin() || state.entityPermissions.rule.update;
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [rules, setRules] = useState<Rule[]>([]);
  const [tenantRules, setTenantRules] = useState<Rule[]>([]);
  const [selectedViolation, setSelectedViolation] = useState<Violation>();
  const [showDialog, setShowDialog] = useState<'create' | 'inUse' | null>(null);
  // Violation Lists
  const [activeViolations, setActiveViolations] = useState<Array<Violation>>(
    [],
  );
  const [inactiveViolations, setInactiveViolations] = useState<
    Array<Violation>
  >([]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: t('violation.label_plural', { ns: 'entities' }),
    });
  }, [pathname]);

  useEffect(() => {
    // if we have the data, we can set the active and inactive violations
    const awaitData = async () => {
      const inactive: Violation[] = [];
      const active: Violation[] = [];

      const tenantViolations = await _tenantViolations;
      tenantViolations.forEach((tv) => {
        if (tv.status === Status.Disabled) {
          inactive.push({ ...tv });
        } else {
          active.push({ ...tv });
        }
      });

      const tenantRules = await _tenantRules;
      // don't list 'All Violations' on this page
      const standardViolations = state.standardViolations.filter(
        (v) => v.id !== 46,
      );
      const excludedViolations = await _excludedViolations;
      standardViolations.forEach((sv) => {
        const ev = excludedViolations.find((ev) => ev.id === sv.id);
        if (ev) {
          inactive.push({ ...sv });
        } else {
          active.push({ ...sv });
        }
      });
      setTenantRules(tenantRules);
      setActiveViolations(active.sort((a, b) => a.name.localeCompare(b.name)));
      setInactiveViolations(
        inactive.sort((a, b) => a.name.localeCompare(b.name)),
      );
    };
    awaitData();
  }, [_excludedViolations, _tenantViolations, state.standardViolations]);

  const response = fetcher.data as ViolationsActionResponse;
  useEffect(() => {
    if (response) {
      if (!response.success && response.violation) {
        // if there was an error, revert the changes
        handleRevertFailedAction(response.handler, response.violation);
        displayToast({ type: TOAST_TYPE.ERROR, message: response.message });
      } else if (
        response.handler == ViolationsActionHandlers.CREATE &&
        response.success
      ) {
        // we need the id for update after we create the violation
        addItemToActive(response.violation as Violation);
        displayToast({ type: TOAST_TYPE.SUCCESS, message: response.message });
      }
    }
  }, [fetcher]);

  const handleRevertFailedAction = (
    handler: ViolationsActionHandlers,
    violation: Violation,
  ) => {
    switch (handler) {
      case ViolationsActionHandlers.CREATE:
        removeItemFromActive(violation);
        setName(violation.name);
        setDescription(violation.description || '');
        break;
      case ViolationsActionHandlers.EXCLUDE:
        moveItemFromInactiveToActive(violation);
        break;
      case ViolationsActionHandlers.INCLUDE:
        moveItemFromActiveToInactive(violation);
        break;
      case ViolationsActionHandlers.DELETE:
        moveItemFromActiveToInactive(violation);
        break;
    }
  };
  const handleIncludeViolation = (item: Violation) => {
    moveItemFromInactiveToActive(item);
    fetcher.submit(
      {
        handler: ViolationsActionHandlers.INCLUDE,
        data: { ...item },
        tenantID: state.tenant.id,
      },
      {
        method: 'PUT',
        action: ROUTES.VIOLATIONS,
        encType: 'application/json',
      },
    );
  };
  const handleExcludeViolation = (item: Violation) => {
    setSelectedViolation(item);
    const r: Rule[] = [];
    if (tenantRules.length > 0) {
      tenantRules.forEach(({ data: { violations } }, index) => {
        if (violations && violations.length > 0) {
          violations.forEach((v) => {
            if (v.id === item.id && v.name === item.name && v.id !== 46) {
              r.push(tenantRules[index]);
            }
          });
        }
      });
    }
    if (r.length > 0) {
      setRules(r);
      setShowDialog('inUse');
    } else {
      moveItemFromActiveToInactive(item);
      fetcher.submit(
        {
          handler: ViolationsActionHandlers.EXCLUDE,
          data: { ...item },
          tenantID: state.tenant.id,
        },
        {
          method: 'PUT',
          action: ROUTES.VIOLATIONS,
          encType: 'application/json',
        },
      );
    }
  };
  const handleCreateViolation = () => {
    const newViolation = {
      id: 0,
      name: name,
      description: description,
      status: Status.Active,
      tenantID: state.tenant.id,
    };
    fetcher.submit(
      {
        handler: ViolationsActionHandlers.CREATE,
        data: newViolation,
      },
      {
        method: 'POST',
        action: ROUTES.VIOLATIONS,
        encType: 'application/json',
      },
    );
    setShowDialog(null);
  };
  const handleCloseDialog = () => {
    setShowDialog(null);
  };
  const handleDeleteViolation = (v: Violation) => {
    removeItemFromInactive(v);
    fetcher.submit(
      {
        handler: ViolationsActionHandlers.DELETE,
        data: { ...v },
        tenantID: state.tenant.id,
      },
      {
        method: 'DELETE',
        action: ROUTES.VIOLATIONS,
        encType: 'application/json',
      },
    );
  };
  const handleOverrideWarning = (item: Violation) => {
    moveItemFromActiveToInactive(item);
    setShowDialog(null);
  };
  const addItemToActive = (item: Violation) => {
    setActiveViolations((prev) => [item, ...prev]);
  };
  const moveItemFromInactiveToActive = (item: Violation) => {
    removeItemFromInactive(item);
    setActiveViolations((prev) => [item, ...prev]);
  };
  const moveItemFromActiveToInactive = (item: Violation) => {
    removeItemFromActive(item);
    setInactiveViolations((prev) => [item, ...prev]);
  };
  const removeItemFromActive = (item: Violation) => {
    setActiveViolations((prev) =>
      prev.filter((v) => v.id !== item.id && v.name !== item.name),
    );
  };
  const removeItemFromInactive = (item: Violation) => {
    setInactiveViolations((prev) =>
      prev.filter((v) => v.id !== item.id && v.name !== item.name),
    );
  };

  if (isMobile) {
    return (
      <React.Fragment>
      {showDialog === 'create' && (
        <CreateViolation
          open={showDialog === 'create'}
          handleCancel={handleCloseDialog}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          handleSubmit={handleCreateViolation}
        />
      )}
      {showDialog === 'inUse' && (
        <ViolationInUseDialog
          handleCancel={handleCloseDialog}
          onConfirm={() =>
            handleOverrideWarning(selectedViolation as Violation)
          }
          open={showDialog === 'inUse'}
          violation={selectedViolation}
          rules={rules}
        />
      )}
        <Suspense
          fallback={<MobileViolationsSkeleton allowCreate={allowCreate} />}
        >
          <Await resolve={_tenantViolations}>
            {() => (
              <MobileViolations
                activeViolations={activeViolations}
                inactiveViolations={inactiveViolations}
                allowUpdate={allowUpdate}
                allowCreate={allowCreate}
                name={name}
                description={description}
                setName={setName}
                setDescription={setDescription}
                handleCreateViolation={handleCreateViolation}
                handleIncludeViolation={handleIncludeViolation}
                handleExcludeViolation={handleExcludeViolation}
                handleDeleteViolation={handleDeleteViolation}
              />
            )}
          </Await>
        </Suspense>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {showDialog === 'create' && (
        <CreateViolation
          open={showDialog === 'create'}
          handleCancel={handleCloseDialog}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          handleSubmit={handleCreateViolation}
        />
      )}
      {showDialog === 'inUse' && (
        <ViolationInUseDialog
          handleCancel={handleCloseDialog}
          onConfirm={() =>
            handleOverrideWarning(selectedViolation as Violation)
          }
          open={showDialog === 'inUse'}
          violation={selectedViolation}
          rules={rules}
        />
      )}
      <FormFrame loading={false}>
        <DetailsHeader
          title={`${t('manage')} ${t('violation.label_plural', {
            ns: 'entities',
          })}`}
          submitLabel={t('buttons.create', {
            name: t('violation.label', { ns: 'entities' }),
          })}
          submitButtonIcon={allowCreate ? <Add /> : <Lock />}
          onSubmit={() => setShowDialog('create')}
          disableSubmit={!allowCreate || fetcher.state !== 'idle'}
        />

        <Stack
          spacing={8}
          direction={'row'}
          width={'100%'}
          display={'flex'}
          p={4}
        >
          <Stack spacing={2} direction={'column'} flex={1}>
            <Divider textAlign='center' role='presentation'>
              <Typography variant='overline' sx={{ fontWeight: 'bold' }}>
                {`${t('inactive')} ${t('violation.label_plural', {
                  ns: 'entities',
                })}`}
              </Typography>
            </Divider>
            <Box sx={{ maxHeight: `calc(100vh - 564px)`, overflowY: 'scroll' }}>
              <Suspense
                fallback={
                  <List
                    aria-label={`${t('inactive')} ${t(
                      'violation.label_plural',
                      { ns: 'entities' },
                    )}`}
                  >
                    {Array.from({ length: 5 }).map((_, index) => (
                      <ViolationListItemSkeleton
                        active={false}
                        sx={smallTableRowSx(index, theme)}
                        key={index}
                      />
                    ))}
                  </List>
                }
              >
                <Await resolve={_tenantViolations}>
                  {() => (
                    <List
                      aria-label={`${t('inactive')} ${t(
                        'violation.label_plural',
                        { ns: 'entities' },
                      )}`}
                      disablePadding
                      dense
                    >
                      {inactiveViolations.map(
                        (item: Violation, index: number) => {
                          return (
                            <ViolationListItem
                              key={`${item.id}${item.name}${item.description}`}
                              item={item}
                              active={false}
                              sx={smallTableRowSx(index, theme)}
                              onMoveClick={handleIncludeViolation}
                              onDeleteClick={handleDeleteViolation}
                              disabled={!allowUpdate}
                              canUpdateViolations={allowUpdate}
                            />
                          );
                        },
                      )}
                    </List>
                  )}
                </Await>
              </Suspense>
            </Box>
          </Stack>
          <Stack spacing={2} direction={'column'} flex={1}>
            <Divider textAlign='center' role='presentation'>
              <Typography variant='overline' sx={{ fontWeight: 'bold' }}>
                {`${t('active')} ${t('violation.label_plural', {
                  ns: 'entities',
                })}`}
              </Typography>
            </Divider>

            <Box sx={{ maxHeight: `calc(100vh - 564px)`, overflowY: 'scroll' }}>
              <Suspense
                fallback={
                  <List
                    aria-label={`${t('active')} ${t('violation.label_plural', {
                      ns: 'entities',
                    })}`}
                    disablePadding
                    dense
                  >
                    {Array.from({ length: 24 }).map((_, index) => (
                      <ViolationListItemSkeleton
                        active={true}
                        sx={smallTableRowSx(index, theme)}
                        key={index}
                      />
                    ))}
                  </List>
                }
              >
                <Await resolve={_excludedViolations}>
                  {() => (
                    <List
                      aria-label={`${t('active')} ${t(
                        'violation.label_plural',
                        { ns: 'entities' },
                      )}`}
                      disablePadding
                      dense
                    >
                      {activeViolations.map(
                        (item: Violation, index: number) => {
                          return (
                            <ViolationListItem
                              key={`${item.id}${item.name}${item.description}`}
                              item={item}
                              active={true}
                              sx={smallTableRowSx(index, theme)}
                              onMoveClick={handleExcludeViolation}
                              canUpdateViolations={allowUpdate}
                              disabled={!allowUpdate}
                            />
                          );
                        },
                      )}
                    </List>
                  )}
                </Await>
              </Suspense>
            </Box>
          </Stack>
        </Stack>
      </FormFrame>
    </React.Fragment>
  );
};

export default Violations;
